import * as Sentry from '@sentry/capacitor';
import type { Primitive, Attachment } from '@sentry/types';

export const getMapDebugSentryTags = (
  map: mapboxgl.Map,
): Record<string, Primitive> => {
  // get the webgl context from the map painter
  const { gl } = map.painter.context;
  // get the webgl version using the WEBGL_debug_renderer_info extension
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const webglVendorVersion = debugInfo
    ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
    : 'unknown';
  const webglRendererVersion = debugInfo
    ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    : 'unknown';

  // get the "MAP_LAYERS" key from localStorage
  const mapLayers = localStorage.getItem('MAP_LAYERS');
  const mapLayersParsed = mapLayers ? JSON.parse(mapLayers) : {};
  const mapLayersTags = Object.keys(mapLayersParsed).reduce(
    (acc, key) => ({
      ...acc,
      [`map.layers.${key}`]: mapLayersParsed[key],
    }),
    {},
  );

  const allTags = {
    'map.isStyleLoaded': !!map.isStyleLoaded(),
    'map.loaded': map.loaded(),
    'map.areTilesLoaded': map.areTilesLoaded(),
    'map.styleName': map.getStyle()?.name,
    ...mapLayersTags,
    'webgl.version': gl.getParameter(gl.VERSION),
    'webgl.vendorVersion': webglVendorVersion,
    'webgl.rendererVersion': webglRendererVersion,
    'webgl.shadingLanguageVersion': gl.getParameter(
      gl.SHADING_LANGUAGE_VERSION,
    ),
    'webgl.vendor': gl.getParameter(gl.VENDOR),
    'webgl.renderer': gl.getParameter(gl.RENDERER),
    'webgl.extensions': gl
      .getSupportedExtensions()
      ?.join(',')
      .substring(0, 200), // max Sentry tag length is 200
    'webgl.maxCombinedTextures': gl.getParameter(
      gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS,
    ),
    'webgl.viewport': gl.getParameter(gl.VIEWPORT).toString(),
    'webgl.activeTexture': gl.getParameter(gl.ACTIVE_TEXTURE),
    'webgl.maxTextureSize': gl.getParameter(gl.MAX_TEXTURE_SIZE),
  };
  return allTags;
};

const captureMapCanvasAsPng = (
  map: mapboxgl.Map,
): Promise<ArrayBuffer | null> => {
  return new Promise((resolve, _) => {
    map.once('render', () => {
      map.getCanvas().toBlob(
        (blob) => {
          if (blob === null) {
            Sentry.captureMessage('debug-data-mapboxgl-capture-canvas-failed');
            resolve(null);
            return;
          }
          blob.arrayBuffer().then(resolve);
        },
        'image/png',
        1.0,
      );
    });
    map.triggerRepaint();
  });
};

export const getMapDebugSentryAttachments = async (
  map: mapboxgl.Map,
): Promise<Array<Attachment>> => {
  const canvasPng = await captureMapCanvasAsPng(map);
  if (!canvasPng) {
    return [];
  }

  return [
    {
      filename: 'map-canvas.png',
      data: new Uint8Array(canvasPng),
      contentType: 'image/png',
    },
  ];
};

// Registers for the webglcontextlost and restored events on the map
// and returns a funciton to remove the listeners.
export const registerMapEventListeners = (map: mapboxgl.Map): (() => void) => {
  const onWebGLContextLost = (): void => {
    Sentry.captureMessage('debug-data-mapboxgl-webgl-context-lost');
  };
  const onWebGLContextRestored = (): void => {
    Sentry.captureMessage('debug-data-mapboxgl-webgl-context-restored');
  };
  map.on('webglcontextlost', onWebGLContextLost);
  map.on('webglcontextrestored', onWebGLContextRestored);
  return () => {
    map.off('webglcontextlost', onWebGLContextLost);
    map.off('webglcontextrestored', onWebGLContextRestored);
  };
};
